import {
  createTranslationMessages,
  createTranslationRef,
} from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';

/**
 * Default (English) messages of the home page.
 *
 * Only the words that belong to this page. The ArgoCD and CI vocabulary
 * (health, sync, pipeline statuses, « Ouvrir Odoo », the Grafana link) is read
 * from `projectEnvironmentsTranslationRef`, so the home page and the
 * environments card can never call the same state two different names.
 *
 * Same arrangement as `components/projectEnvironments/translation.ts`: the
 * French resource below overrides the default language, with `full: true`,
 * and is registered by a module on the `app` plugin (see `plugin.tsx`).
 */
export const homeTranslationRef = createTranslationRef({
  id: 'waadoo-home',
  messages: {
    // The BCP 47 locale of the relative times and the figures of the page —
    // see `format.locale` in the environments translation for why it is a
    // message and not the app language.
    'format.locale': 'en',
    'header.greeting': 'Hello {{name}}',
    'header.greetingAnonymous': 'Hello',
    'header.refresh': 'Refresh',
    'search.label': 'Search the platform',
    'search.placeholder': 'Projects, documentation, people…',
    // The same on a phone, where the field is narrower: never cut.
    'search.placeholderShort': 'Projects, documentation…',
    // « À surveiller » in the header of « Projets » (WatchList.tsx): the
    // pill, a button that opens the list of what to watch.
    'watch.pill_one': '{{count}} to watch',
    'watch.pill_other': '{{count}} to watch',
    'watch.listLabel': 'Environments to watch',
    'watch.pipelineFailed': 'Pipeline failed',
    // Whole days, "N d" — see `branch.description` in the environments
    // translation for why there is no plural form.
    'watch.inactiveFor': 'No new deployment for {{days}} d',
    'watch.deleteReminder':
      'Consider deleting the branch if it is no longer needed.',
    // « Projets », the home page's table (ProjectsTable.tsx): its title, its
    // counts, the pill of the pipelines running, its columns.
    'projects.title': 'Projects',
    'projects.environmentsCount_one': '{{count}} environment',
    'projects.environmentsCount_other': '{{count}} environments',
    'projects.ephemeral_one': '{{count}} ephemeral',
    'projects.ephemeral_other': '{{count}} ephemeral',
    'projects.runningPill_one': '{{count}} pipeline running',
    'projects.runningPill_other': '{{count}} pipelines running',
    'projects.runningListLabel': 'Pipelines running',
    // The ArgoCD Applications no row shows, after the counts.
    'projects.unlisted_one': '+ {{count}} outside a project',
    'projects.unlisted_other': '+ {{count}} outside a project',
    'projects.unlistedHint':
      'ArgoCD Applications attached to no listed project, or without a readable branch',
    'projects.column.project': 'Project',
    'projects.column.environments': 'Environments',
    'projects.column.state': 'State',
    'projects.column.pipeline': 'Latest pipeline',
    'projects.column.createdBy': 'Created by',
    'projects.column.links': 'Links',
    'projects.column.actions': 'Actions',
    // An environment chip's accessible name and tooltip: the branch and its
    // state, said by the dot otherwise.
    'projects.envChip': '{{branch}} ({{state}})',
    'projects.envChipEphemeral': '{{branch}} ({{state}}, ephemeral)',
    // A sleeping permanent environment's chip (permanentEnvironments.ts):
    // its accessible name and tooltip.
    'projects.envChipSleeping': '{{branch}} · asleep',
    // On a phone, a project's line (ProjectsTable.tsx): a sleeping
    // environment's dot, its accessible name; an awake one's is `envChip`.
    'projects.envDotSleeping': '{{branch}} asleep',
    'projects.empty': 'You do not have any project yet.',
    'projects.emptyHint': 'Create your first project to get started.',
    // The secondary button in the header of « Projets », a link to the
    // « Créer » page — the same name as the sidebar's entry it leads to.
    'projects.createAction': 'Create',
    // The dashed slot shown when there is no project yet (CreateSlot),
    // leading where the header's button does: its title, then its subtitle;
    // its accessible name is both.
    'projects.create': 'Create a project',
    'projects.createText': 'Choose a template on the « Create » page',
    'projects.error': 'The projects could not be read: {{message}}',
    'projects.environmentsError':
      'The environments could not be read from ArgoCD: {{message}}',
    'projects.noEnvironment': 'No environment',
    'projects.environmentsUnavailable': 'Environments unavailable',
    'projects.environmentsLabel': 'Environments of {{project}}',
    'projects.openRepository': 'Repository of {{project}}',
    'projects.pipelinesCapped':
      'CI status shown for the first {{max}} projects only.',
    // « Dernier pipeline » of a project of a `standard` type (NoCiPill,
    // StatusBits.tsx): the pill, then its tooltip.
    'projects.noCi': 'No CI',
    'projects.noCiHint':
      'Standard type: a public image, no pipeline of the project’s own.',
    // A project's state pill's tooltip: the worst problem, with how many
    // others have one.
    'projects.problem': '{{branch}}: {{status}}',
    'projects.problemOthers_one': '{{problem}}, +{{count}} other',
    'projects.problemOthers_other': '{{problem}}, +{{count}} others',
    'projects.stateLoading': 'Loading',
    'projects.stateUnavailable': 'Unavailable',
    // The « ⋮ » menu of a project row (ProjectActionsMenu.tsx): its
    // button's name, then its entries. `forge`: « GitLab » or « GitHub ».
    // « New branch… » for the project's editors and admins, « Delete the
    // project… » for admins only; the alert when the branch dialog cannot
    // open.
    'projects.actions': 'Actions for project {{project}}',
    'projects.openPage': 'Open the project page',
    'projects.repositoryEntry': '{{forge}} repository',
    'projects.newBranch': 'New branch…',
    'projects.newBranchUnavailable':
      'A branch cannot be created in {{project}} right now.',
    'projects.delete': 'Delete the project…',
    // The dialog « Delete the project… » opens (DeleteProjectDialog.tsx). The
    // template is named by its own title, which is not translated.
    'deleteProject.title': 'Delete project {{project}}',
    'deleteProject.subtitle':
      'Opens the « Supprimer un projet {{app}} » template, prefilled',
    'deleteProject.modeLabel': 'Kind of deletion',
    'deleteProject.simulation': 'Simulation (recommended)',
    'deleteProject.simulationText':
      'Reads everything and describes what would be deleted, without changing anything.',
    'deleteProject.deletion': 'Actual deletion',
    'deleteProject.deletionText':
      'Repositories, environments, databases and backups. The template will still ask you to accept the data loss and to retype the project name.',
    'deleteProject.cancel': 'Cancel',
    'deleteProject.continue': 'Continue to the template',
    'env.openAppFor': 'Open {{app}} ({{branch}})',
    'env.grafanaFor': 'Monitoring of {{branch}} (Grafana)',
    'env.pipelineFor': 'Latest pipeline of {{branch}}: {{status}}',
    'platform.title': 'Platform',
    // The platform strip's cells (PlatformStrip.tsx); CPU and memory reuse
    // `platform.cpu` / `platform.memory`, the components `components.title`.
    // The state: Healthy, To watch, Degraded (`platformState`).
    'platform.stateHealthy': 'Healthy',
    'platform.stateWarning': 'To watch',
    'platform.stateFailed': 'Degraded',
    'platform.nodesRow': 'Nodes',
    // Under « 3 / 3 »: « ready · 1 pool »; under « 14 / 14 »: « healthy ».
    'platform.readyWord_one': 'ready',
    'platform.readyWord_other': 'ready',
    'platform.poolCount_one': '{{count}} pool',
    'platform.poolCount_other': '{{count}} pools',
    // On a phone, under the state of « Plateforme », in place of the
    // « Nœuds » cell: ready over all.
    'platform.nodesReady_one': '{{ready}}/{{count}} node ready',
    'platform.nodesReady_other': '{{ready}}/{{count}} nodes ready',
    'platform.healthyWord_one': 'healthy',
    'platform.healthyWord_other': 'healthy',
    'platform.cpuFigures': '{{requested}} / {{allocatable}} vCPU',
    'platform.memoryFigures': '{{requested}} / {{allocatable}} GiB',
    'platform.unavailable': 'Unavailable',
    'platform.cpu': 'CPU requested',
    'platform.memory': 'Memory requested',
    // The node rows of the « Nodes » drawer (PlatformNodes.tsx). `node`:
    // the node's short id, « 8xjnh ».
    'platform.nodeCpu': 'CPU of {{node}}',
    'platform.nodeMemory': 'Memory of {{node}}',
    'platform.nodeCpuShort': 'CPU',
    'platform.nodeMemoryShort': 'Mem.',
    // `letter`: the zone's last segment, « a » for eu-west-par-a.
    'platform.zone': 'Zone {{letter}}',
    'platform.zoneUnknown': 'Unknown zone',
    // Under a zone's header: its nodes' requested over allocatable, the
    // `platform.cpuFigures` and `platform.memoryFigures` of the cells.
    'platform.zoneSum': 'zone: {{cpu}} · {{memory}}',
    // A place the zone's autoscaled pool may still fill, as a dashed row.
    'platform.freeSlot': 'free slot — added by the autoscaler if needed',
    // The legend of the bars' colours (WARNING_PERCENT, CRITICAL_PERCENT), then
    // what the bars measure.
    'platform.legendOk': '< {{warning}}%',
    'platform.legendWarning': '{{warning}}–{{below}}%',
    'platform.legendCritical': '≥ {{critical}}%',
    'platform.legendBasis': 'requested / allocatable',
    // A node tile's colour, in words, for a screen reader.
    'platform.levelOkName': 'normal load',
    'platform.levelWarningName': 'high load',
    'platform.levelCriticalName': 'critical load',
    // A node that joined the cluster less than a day ago (`isNewNode`).
    'platform.nodeNew': 'new',
    // The autoscaled pools' room left, when the zone cards replace the pools
    // block (which says « Autoscaling headroom: 8 nodes of 9 at most »).
    'platform.autoscalingMargin_one': 'Autoscaling headroom: {{count}} node',
    'platform.autoscalingMargin_other': 'Autoscaling headroom: {{count}} nodes',
    // The machine's size: vCPU, and the memory rounded as the flavour is
    // sold (`toSoldGiB`, platform.ts).
    'platform.nodeVcpu': '{{cpu}} vCPU',
    'platform.nodeMemorySold': '{{memory}} GB',
    'platform.nodePods_one': '{{count}} pod',
    'platform.nodePods_other': '{{count}} pods',
    // The node title's accessible name: its label, then its tooltip's
    // sentence (full name, pool, what the pods may request).
    'platform.nodeName': 'Node {{node}}',
    'platform.nodeTitleName': '{{label}} — {{text}}',
    'platform.nodePool': 'node pool {{pool}}',
    'platform.nodeAvailable':
      'available to pods: {{cpu}} vCPU · {{memory}} GiB',
    // A node's uptime counts since its Ready condition last turned True
    // (reset by a reboot or a kubelet restart); the dates in its tooltip.
    'platform.nodeReadySince': 'Ready since {{date}}',
    'platform.nodeCreated': 'joined on {{date}}',
    'platform.nodeFlavor': 'OVH instance {{flavor}}',
    'platform.nodeKubelet': 'kubelet {{version}}',
    // A node's alerts (its kubelet conditions, a cordon), as status chips.
    'platform.alert.notReady': 'Not ready',
    'platform.alert.diskPressure': 'Disk pressure',
    'platform.alert.memoryPressure': 'Memory pressure',
    'platform.alert.pidPressure': 'PID pressure',
    'platform.alert.networkUnavailable': 'Network unavailable',
    'platform.alert.cordoned': 'Cordoned',
    'platform.clusterUnavailable':
      'The cluster capacity cannot be read right now.',
    // The cluster line of the « Nodes » drawer (PlatformCluster.tsx). A
    // figure the nodes report with several values reads « 2 versions »,
    // the values in its tooltip; `label` names the figure there.
    'platform.kubernetesVersion': 'Kubernetes {{version}}',
    'platform.versionCount_one': '{{count}} version',
    'platform.versionCount_other': '{{count}} versions',
    'platform.regionCount_one': '{{count}} region',
    'platform.regionCount_other': '{{count}} regions',
    'platform.factTooltip': '{{label}}: {{text}}',
    'platform.runtimeLabel': 'Container runtime',
    'platform.osLabel': 'Operating system',
    'platform.regionLabel': 'Region',
    'platform.kernel': 'kernel {{version}}',
    // Kubelets that differ among themselves or from the API server; the
    // tooltip says which nodes run which kubelet.
    'platform.versionsMixed': 'Mixed versions',
    'platform.versionsAligned': 'API server and kubelet versions aligned',
    'platform.apiServerVersion': 'API server {{version}}',
    'platform.kubeletNodes': 'kubelet {{version}}: {{nodes}}',
    // The « Node pools » block (PlatformPools.tsx), OVH's NodePools.
    'platform.pools': 'Node pools',
    'platform.poolNodes_one': '{{count}} node',
    'platform.poolNodes_other': '{{count}} nodes',
    'platform.poolTarget': 'target {{number}}',
    'platform.poolMin': 'min {{number}}',
    'platform.poolMax': 'max {{number}}',
    'platform.autoscaling': 'Autoscaling',
    'platform.poolMeter': 'Nodes',
    'platform.poolMeterName': 'Nodes of {{pool}}',
    'platform.poolMeterValue_one': '{{current}} of {{count}} node',
    'platform.poolMeterValue_other': '{{current}} of {{count}} nodes',
    'platform.poolUnavailable_one': '{{count}} node unavailable',
    'platform.poolUnavailable_other': '{{count}} nodes unavailable',
    'platform.poolOutdated_one': '{{count}} node to update',
    'platform.poolOutdated_other': '{{count}} nodes to update',
    // Summed over the autoscaled pools: current nodes against their maximum.
    'platform.autoscalingHeadroom_one':
      'Autoscaling headroom: {{count}} node of {{max}} at most',
    'platform.autoscalingHeadroom_other':
      'Autoscaling headroom: {{count}} nodes of {{max}} at most',
    // « Components » (PlatformComponentList.tsx), admins only: the platform's own
    // software by group, its version and its ready / desired pods.
    'components.title': 'Components',
    'components.unavailable':
      'The platform components cannot be read right now.',
    'components.empty': 'No known component was found on this cluster.',
    'components.group.access': 'Access',
    'components.group.identity': 'Identity',
    'components.group.gitops': 'GitOps',
    'components.group.observability': 'Observability',
    'components.group.data': 'Data',
    'components.group.storage': 'Storage',
    'components.group.mail': 'Mail',
    'components.group.network': 'Network',
    // The rows named in words rather than by a product; the others show the
    // backend's `displayName` as is.
    'components.name.webhookOvh': 'OVH webhook (cert-manager)',
    'components.name.nfsDriver': 'NFS driver (csi-driver-nfs)',
    'components.name.nfsProvisioner': 'NFS provisioner',
    'components.name.smtpRelay': 'SMTP relay',
    // The health chip: ready / desired pods.
    'components.healthy': 'Healthy',
    'components.degraded': 'Degraded {{ready}}/{{desired}}',
    'components.stopped': 'Stopped {{ready}}/{{desired}}',
    // The version badge's tooltip, then its accessible name (with
    // `platform.nodeTitleName`). `kind`: Deployment, StatefulSet, DaemonSet.
    'components.version': 'Version {{version}}',
    'components.workload': '{{kind}} {{namespace}}/{{name}}',
    'components.chart': 'Chart: {{chart}}',
    'components.imageTag': 'Image: {{tag}}',
    'components.labelVersion': 'Label: {{version}}',
    'components.versionMismatch':
      'The label says {{label}}, the image {{image}}',
  },
});

/**
 * The French messages. Typographic apostrophe (’) throughout, as in the
 * environments translation.
 */
const resource = createTranslationMessages({
  full: true,
  ref: homeTranslationRef,
  messages: {
    'format.locale': 'fr-FR',
    'header.greeting': 'Bonjour {{name}}',
    'header.greetingAnonymous': 'Bonjour',
    'header.refresh': 'Rafraîchir',
    'search.label': 'Rechercher dans la plateforme',
    'search.placeholder': 'Projets, documentation, personnes…',
    'search.placeholderShort': 'Projets, documentation…',
    'watch.pill_one': '{{count}} à surveiller',
    'watch.pill_other': '{{count}} à surveiller',
    'watch.listLabel': 'Environnements à surveiller',
    'watch.pipelineFailed': 'Pipeline en échec',
    'watch.inactiveFor': 'Aucun nouveau déploiement depuis {{days}} j',
    'watch.deleteReminder':
      'Pensez à supprimer la branche si elle n’est plus utile.',
    'projects.title': 'Projets',
    'projects.environmentsCount_one': '{{count}} environnement',
    'projects.environmentsCount_other': '{{count}} environnements',
    'projects.ephemeral_one': '{{count}} éphémère',
    'projects.ephemeral_other': '{{count}} éphémères',
    'projects.runningPill_one': '{{count}} pipeline en cours',
    'projects.runningPill_other': '{{count}} pipelines en cours',
    'projects.runningListLabel': 'Pipelines en cours',
    'projects.unlisted_one': '+ {{count}} hors projet',
    'projects.unlisted_other': '+ {{count}} hors projet',
    'projects.unlistedHint':
      'Applications ArgoCD rattachées à aucun projet listé, ou sans branche lisible',
    'projects.column.project': 'Projet',
    'projects.column.environments': 'Environnements',
    'projects.column.state': 'État',
    'projects.column.pipeline': 'Dernier pipeline',
    'projects.column.createdBy': 'Créé par',
    'projects.column.links': 'Liens',
    'projects.column.actions': 'Actions',
    'projects.envChip': '{{branch}} ({{state}})',
    'projects.envChipEphemeral': '{{branch}} ({{state}}, éphémère)',
    'projects.envChipSleeping': '{{branch}} · en sommeil',
    'projects.envDotSleeping': '{{branch}} en sommeil',
    'projects.empty': 'Vous n’avez encore aucun projet.',
    'projects.emptyHint': 'Créez votre premier projet pour commencer.',
    'projects.createAction': 'Créer',
    'projects.create': 'Créer un projet',
    'projects.createText': 'Choisissez un modèle sur la page « Créer »',
    'projects.error': 'Les projets n’ont pas pu être lus : {{message}}',
    'projects.environmentsError':
      'Les environnements n’ont pas pu être lus dans ArgoCD : {{message}}',
    'projects.noEnvironment': 'Aucun environnement',
    'projects.environmentsUnavailable': 'Environnements indisponibles',
    'projects.environmentsLabel': 'Environnements de {{project}}',
    'projects.openRepository': 'Dépôt de {{project}}',
    'projects.pipelinesCapped':
      'Statut CI affiché pour les {{max}} premiers projets seulement.',
    'projects.noCi': 'Sans CI',
    'projects.noCiHint':
      'Type standard : image publique, pas de pipeline propre au projet.',
    'projects.problem': '{{branch}} : {{status}}',
    'projects.problemOthers_one': '{{problem}}, +{{count}} autre',
    'projects.problemOthers_other': '{{problem}}, +{{count}} autres',
    'projects.stateLoading': 'Chargement',
    'projects.stateUnavailable': 'Indisponible',
    'projects.actions': 'Actions du projet {{project}}',
    'projects.openPage': 'Ouvrir la page du projet',
    'projects.repositoryEntry': 'Dépôt {{forge}}',
    'projects.newBranch': 'Nouvelle branche…',
    'projects.newBranchUnavailable':
      'Impossible de créer une branche dans {{project}} pour le moment.',
    'projects.delete': 'Supprimer le projet…',
    'deleteProject.title': 'Supprimer le projet {{project}}',
    'deleteProject.subtitle':
      'Ouvre le modèle « Supprimer un projet {{app}} » pré-rempli',
    'deleteProject.modeLabel': 'Type de suppression',
    'deleteProject.simulation': 'Simulation (recommandée)',
    'deleteProject.simulationText':
      'Lit tout et décrit ce qui serait supprimé, sans rien modifier.',
    'deleteProject.deletion': 'Suppression réelle',
    'deleteProject.deletionText':
      'Dépôts, environnements, bases et sauvegardes. Le modèle demandera encore d’accepter la perte des données et de retaper le nom du projet.',
    'deleteProject.cancel': 'Annuler',
    'deleteProject.continue': 'Continuer vers le modèle',
    'env.openAppFor': 'Ouvrir {{app}} ({{branch}})',
    'env.grafanaFor': 'Monitoring de {{branch}} (Grafana)',
    'env.pipelineFor': 'Dernier pipeline de {{branch}} : {{status}}',
    'platform.title': 'Plateforme',
    'platform.stateHealthy': 'Saine',
    'platform.stateWarning': 'À surveiller',
    'platform.stateFailed': 'Dégradée',
    'platform.nodesRow': 'Nœuds',
    'platform.readyWord_one': 'prêt',
    'platform.readyWord_other': 'prêts',
    'platform.poolCount_one': '{{count}} pool',
    'platform.poolCount_other': '{{count}} pools',
    'platform.nodesReady_one': '{{ready}}/{{count}} nœud prêt',
    'platform.nodesReady_other': '{{ready}}/{{count}} nœuds prêts',
    'platform.healthyWord_one': 'sain',
    'platform.healthyWord_other': 'sains',
    // « vCPU », invariable et identique en anglais (décision du 2026-10-01) :
    // la même unité que la carte « Ressources » des pages Odoo.
    'platform.cpuFigures': '{{requested}} / {{allocatable}} vCPU',
    'platform.memoryFigures': '{{requested}} / {{allocatable}} Gio',
    'platform.unavailable': 'Indisponible',
    'platform.cpu': 'CPU demandé',
    'platform.memory': 'Mémoire demandée',
    'platform.nodeCpu': 'CPU de {{node}}',
    'platform.nodeMemory': 'Mémoire de {{node}}',
    'platform.nodeCpuShort': 'CPU',
    'platform.nodeMemoryShort': 'Mém.',
    'platform.zone': 'Zone {{letter}}',
    'platform.zoneUnknown': 'Zone inconnue',
    'platform.zoneSum': 'zone : {{cpu}} · {{memory}}',
    'platform.freeSlot': 'place libre — ajoutée par l’autoscaling si besoin',
    'platform.legendOk': '< {{warning}} %',
    'platform.legendWarning': '{{warning}}–{{below}} %',
    'platform.legendCritical': '≥ {{critical}} %',
    'platform.legendBasis': 'demandé / allouable',
    'platform.levelOkName': 'charge normale',
    'platform.levelWarningName': 'charge élevée',
    'platform.levelCriticalName': 'charge critique',
    'platform.nodeNew': 'nouveau',
    'platform.autoscalingMargin_one': 'Marge d’autoscaling : {{count}} nœud',
    'platform.autoscalingMargin_other': 'Marge d’autoscaling : {{count}} nœuds',
    'platform.nodeVcpu': '{{cpu}} vCPU',
    // « Go », l’unité commerciale de la gamme ; les barres restent en Gio.
    'platform.nodeMemorySold': '{{memory}} Go',
    'platform.nodePods_one': '{{count}} pod',
    'platform.nodePods_other': '{{count}} pods',
    'platform.nodeName': 'Nœud {{node}}',
    'platform.nodeTitleName': '{{label}} — {{text}}',
    'platform.nodePool': 'pool de nœuds {{pool}}',
    'platform.nodeAvailable':
      'disponible pour les pods : {{cpu}} vCPU · {{memory}} Gio',
    'platform.nodeReadySince': 'prêt depuis le {{date}}',
    'platform.nodeCreated': 'ajouté au cluster le {{date}}',
    'platform.nodeFlavor': 'Instance OVH {{flavor}}',
    'platform.nodeKubelet': 'kubelet {{version}}',
    'platform.alert.notReady': 'Non prêt',
    'platform.alert.diskPressure': 'Disque saturé',
    'platform.alert.memoryPressure': 'Mémoire saturée',
    'platform.alert.pidPressure': 'Processus saturés',
    'platform.alert.networkUnavailable': 'Réseau indisponible',
    'platform.alert.cordoned': 'Planification suspendue',
    'platform.clusterUnavailable':
      'La capacité du cluster ne peut pas être lue pour le moment.',
    'platform.kubernetesVersion': 'Kubernetes {{version}}',
    'platform.versionCount_one': '{{count}} version',
    'platform.versionCount_other': '{{count}} versions',
    'platform.regionCount_one': '{{count}} région',
    'platform.regionCount_other': '{{count}} régions',
    'platform.factTooltip': '{{label}} : {{text}}',
    'platform.runtimeLabel': 'Moteur de conteneurs',
    'platform.osLabel': 'Système d’exploitation',
    'platform.regionLabel': 'Région',
    'platform.kernel': 'noyau {{version}}',
    'platform.versionsMixed': 'Versions mélangées',
    'platform.versionsAligned':
      'Versions du serveur d’API et des kubelets alignées',
    'platform.apiServerVersion': 'serveur d’API {{version}}',
    'platform.kubeletNodes': 'kubelet {{version}} : {{nodes}}',
    'platform.pools': 'Pools de nœuds',
    'platform.poolNodes_one': '{{count}} nœud',
    'platform.poolNodes_other': '{{count}} nœuds',
    'platform.poolTarget': 'cible {{number}}',
    'platform.poolMin': 'min {{number}}',
    'platform.poolMax': 'max {{number}}',
    'platform.autoscaling': 'Autoscaling',
    'platform.poolMeter': 'Nœuds',
    'platform.poolMeterName': 'Nœuds de {{pool}}',
    'platform.poolMeterValue_one': '{{current}} sur {{count}} nœud',
    'platform.poolMeterValue_other': '{{current}} sur {{count}} nœuds',
    'platform.poolUnavailable_one': '{{count}} nœud indisponible',
    'platform.poolUnavailable_other': '{{count}} nœuds indisponibles',
    'platform.poolOutdated_one': '{{count}} nœud à mettre à jour',
    'platform.poolOutdated_other': '{{count}} nœuds à mettre à jour',
    'platform.autoscalingHeadroom_one':
      'Marge d’autoscaling : {{count}} nœud sur {{max}} au maximum',
    'platform.autoscalingHeadroom_other':
      'Marge d’autoscaling : {{count}} nœuds sur {{max}} au maximum',
    'components.title': 'Composants',
    'components.unavailable':
      'Les composants de la plateforme ne peuvent pas être lus pour le moment.',
    'components.empty': 'Aucun composant connu n’a été trouvé sur ce cluster.',
    'components.group.access': 'Accès',
    'components.group.identity': 'Identité',
    'components.group.gitops': 'GitOps',
    'components.group.observability': 'Observabilité',
    'components.group.data': 'Données',
    'components.group.storage': 'Stockage',
    'components.group.mail': 'Messagerie',
    'components.group.network': 'Réseau',
    'components.name.webhookOvh': 'Webhook OVH (cert-manager)',
    'components.name.nfsDriver': 'Pilote NFS (csi-driver-nfs)',
    'components.name.nfsProvisioner': 'Provisionneur NFS',
    'components.name.smtpRelay': 'Relais SMTP',
    'components.healthy': 'Sain',
    'components.degraded': 'Dégradé {{ready}}/{{desired}}',
    'components.stopped': 'Arrêté {{ready}}/{{desired}}',
    'components.version': 'Version {{version}}',
    'components.workload': '{{kind}} {{namespace}}/{{name}}',
    'components.chart': 'Chart : {{chart}}',
    'components.imageTag': 'Image : {{tag}}',
    'components.labelVersion': 'Étiquette : {{version}}',
    'components.versionMismatch':
      'L’étiquette indique {{label}}, l’image {{image}}',
  },
});

export const homeFrTranslation = TranslationBlueprint.make({
  name: 'waadoo-home-fr',
  params: { resource },
});
